import React from 'react';
import { Box, Button, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { COLOR_GREY, COLOR_WHITE } from '../utilities/MaterialWatchman';
import { ReactComponent as SendingIcon } from '../../resources/images/clock.svg';
import { ReactComponent as WarningIcon } from '../../resources/images/alert-triangle.svg';
import { ReactComponent as SentIcon } from '../../resources/images/sent-check.svg';
import { getCustomColors } from '../../styles/CustomColors';
import { ReactComponent as ResendIcon } from '../../resources/images/refresh-ccw.svg';
import { ReactComponent as DeleteIcon } from '../../resources/images/trash-2.svg';

export const MESSAGETYPE = {
    TEXT: 'text',
    URL: 'url',
    FILE: 'file',
    IMAGE: 'image',
};

export const SENDERTYPE = {
    SELF: 'self',
    OTHER: 'other',
};

export const DELIVERY_STATE = {
    SENDING: 'sending',
    SENT: 'sent',
    NOT_SENT: 'not_sent',
    NA: 'not_applicable'
}

const COLOURS = getCustomColors();

const useStyles = makeStyles(theme => ({
    eventLine: {
        display: 'flex',
        margin: '0.5rem 0',
        '&:last-child': {
            marginBottom: theme.spacing(4),
        },
    },
    bubble: {
        position: 'relative',
    },
    bubbleBody: {
        color: 'white',
        borderRadius: theme.spacing(1),
        padding: `${theme.spacing(1.25)}px ${theme.spacing(2.5)}px`,
        textAlign: 'left',
        margin: '0.2rem 0',
        fontSize: theme.spacing(1.75),
        lineHeight: `${theme.spacing(2.65)}px`,
        marginBottom: theme.spacing(3.4),
        maxWidth: theme.spacing(32.375),
        display: 'inline-block',
        wordBreak: 'break-word',
        '& img': {
            maxWidth: '100%',
        },
    },
    menu: {
        fontSize: '0.6rem',
        display: 'none',
        position: 'absolute',
        top: '-0.6rem',
        right: '0',
        padding: '0.3rem',
        backgroundColor: 'white',
        borderTopLeftRadius: '0.5rem',
        borderTopRightRadius: '0.5rem',
        border: '1px solid #dadada',
        borderBottom: 'none',
        '& button': {
            margin: '0 0.3rem',
            border: '1px solid #ccc',
            color: 'rgb(117, 117, 117)',
            borderRadius: '0.3rem',
        }
    },
    bubbleSender: {
        fontSize: '0.85rem',
        color: 'rgb(167, 167, 167)',
    },
    bubbleSenderName: {
        color: theme.palette.primary.dark,
        fontSize: theme.spacing(1.75),
        fontWeight: 600,
    },
    bubbleSenderTitle: {
        fontSize: theme.spacing(1.25),
        fontWeight: 500,
        color: theme.palette.primary.dark,
    },
    bubbleTime: {
        fontSize: theme.spacing(1.25),
        color: COLOR_GREY.main,
        fontWeight: 600,
    },
    content: {
        display: 'block',
    },
    selfContent: {
        textAlign: 'right',
    },
    otherContent: {
        textAlign: 'left',
    },
    other: {
        justifyContent: 'flex-start',
    },
    otherBubbleBody: {
        backgroundColor: theme.palette.primary.dark,
        color: COLOR_WHITE,
    },
    otherBubbleSender: {
        textAlign: 'left',
    },
    otherBubbleTime: {
        textAlign: 'left',
    },
    self: {
        justifyContent: 'flex-end',
    },
    selfBubbleBody: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.primary.dark,
        textAlign: 'right',
        marginRight: theme.spacing(3.5),
    },
    selfBubbleSender: {
        textAlign: 'right',
    },
    selfBubbleTime: {
        textAlign: 'right',
    },
    initialsAvatarContainer: {
        paddingRight: theme.spacing(1.25),
    },
    initialsAvatar: {
        backgroundColor: theme.palette.primary.dark,
        color: COLOR_WHITE,
        fontSize: theme.spacing(1.75),
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        textTransform: 'uppercase',
        fontWeight: 600,
    },
    imageAvatar: {
        width: '30px',
        height: '30px',
        borderRadius: '50%',
    },
    imageBubbleBody: {
        backgroundColor: 'transparent',
        padding: 0,
        '& img': {
            borderRadius: theme.spacing(1),
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.25)',
        },
    },
    deliveryStatusContainer: {
        marginBottom: theme.spacing(1.25),
        display: 'flex',
        alignItems: 'center',
    },
    deliveryStatusContainerOther: {
        justifyContent: 'flex-start',
    },
    deliveryStatusContainerSelf: {
        justifyContent: 'flex-end',
    },
    deliveryStatusIcon: {
        width: theme.spacing(2.25),
        height: theme.spacing(2.25),
        marginLeft: theme.spacing(1.25),
        display: 'flex',
        color: COLOR_WHITE,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        '& svg': {
            height: theme.spacing(1.15),
            width: theme.spacing(1.15),
        }
    },
    sendingIcon: {
        backgroundColor: COLOR_GREY.main,
    },
    warningIcon: {
        backgroundColor: COLOURS.CHAT_ERROR_COLOUR,
    },
    sentIcon: {
        backgroundColor: theme.palette.primary.main,
    },
    errorSendingText: {
        color: COLOURS.CHAT_ERROR_COLOUR,
    },
    resendDeleteContainer: {
        padding: theme.spacing(2),
        paddingTop: 0,
        paddingRight: theme.spacing(3.5),
    },
    resendDeleteButton: {
        borderRadius: theme.spacing(2),
        color: theme.palette.primary.dark,
        border: `2px solid ${theme.palette.primary.dark}`,
        fontSize: theme.spacing(1.75),
        padding: theme.spacing(1),
        paddingLeft: theme.spacing(1.5),
        height: theme.spacing(3.5),
        fontWeight: 500,
        '& svg': {
            width: theme.spacing(1.75),
            height: theme.spacing(1.75),
        },
        '&:last-child': {
            marginLeft: theme.spacing(2.5),
        },
    },
    unsentMessage: {
        filter: 'grayscale(1)',
    },
}));

const getMemberInitials = (name) => {
    try {
        const letters = name.match(/\b(\w)/g);
        let initials = [];
        if(letters.length > 1) {
            initials = [letters[0], letters[letters.length - 1]].join('');
        } else {
            initials = letters[0];
        }
        return initials;
    }
    catch(e) {
        console.error(e);
        return name ? name[0] : '';
    }
}

function ResendOrDelete({ onResend, onDelete }) {
    const classes = useStyles();

    return (
        <Box className={classes.resendDeleteContainer}>
            <Button onClick={onResend} className={classes.resendDeleteButton} variant="outlined" startIcon={<ResendIcon />}>
                RE-SEND
            </Button>

            <Button onClick={onDelete} className={classes.resendDeleteButton} variant="outlined" startIcon={<DeleteIcon />}>
                DELETE
            </Button>
        </Box>
    )
}

function DeliveryStatusIcon({ time, state, onResend, onDelete }) {

    let StatusIcon = null;
    let iconClassName = null;
    let textContent = '';
    let textClass = null;
    const classes = useStyles();

    switch(state) {
        case DELIVERY_STATE.SENDING:
            StatusIcon = SendingIcon;
            iconClassName = classes.sendingIcon;
            textContent = 'SENDING...';
            break;
        case DELIVERY_STATE.NOT_SENT:
            StatusIcon = WarningIcon;
            iconClassName = classes.warningIcon;
            textContent = 'YOUR MESSAGE WAS NOT DELIVERED.';
            textClass = classes.errorSendingText;
            break;
        default:
            StatusIcon = SentIcon;
            iconClassName = classes.sentIcon;
            textContent = time;
            break;
    }

    return (
        <>
            <Box className={clsx(classes.deliveryStatusContainer, state === DELIVERY_STATE.NA ? classes.deliveryStatusContainerOther : classes.deliveryStatusContainerSelf)}>
                <div className={clsx(classes.bubbleTime, textClass)}>{textContent}</div>
                { state !== DELIVERY_STATE.NA && (<Box className={clsx(classes.deliveryStatusIcon, iconClassName)}><StatusIcon /></Box>) }
            </Box>
            {
                state === DELIVERY_STATE.NOT_SENT && <ResendOrDelete onResend={onResend} onDelete={onDelete} />
            }
        </>
    )
}

function MessageEvent({
    eventId,
    senderType,
    messageType,
    sender,
    time,
    onEdit,
    children,
    title = 'User',
    avatarUrl,
    deliveryState,
    onResend,
    onDelete,
}) {
    const classes = useStyles();
    const isOther = senderType === SENDERTYPE.OTHER;
    
    return (
        <>
            <Box className={clsx(classes.eventLine, classes[senderType])}>
                <Box className={classes.bubble}>
                    <Box display="flex">
                        {
                            isOther &&
                            <Box className={classes.initialsAvatarContainer}>
                                <span className={classes.initialsAvatar}>
                                    {
                                        avatarUrl
                                        ? <img className={classes.imageAvatar} src={avatarUrl} alt={`${sender} avatar ${time}`} />
                                        : getMemberInitials(sender)
                                    }
                                </span>
                            </Box>
                        }
                        <Box className={clsx(classes.content, classes[`${senderType}Content`])}>
                            <div className={clsx(classes.bubbleSender, classes[`${senderType}BubbleSender`])}>
                                {
                                    isOther &&
                                    (<>
                                        <div className={classes.bubbleSenderName}>{sender}</div>
                                        <div className={classes.bubbleSenderTitle}>{title}</div>
                                    </>)
                                }
                                <DeliveryStatusIcon onResend={onResend} onDelete={onDelete} state={isOther ? DELIVERY_STATE.NA : deliveryState} time={time} />
                            </div>
                            <div className={clsx(classes.bubbleBody, classes[`${senderType}BubbleBody`], classes[`${messageType}BubbleBody`], { [classes.unsentMessage]: deliveryState === DELIVERY_STATE.NOT_SENT })}>
                                {children}
                            </div>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default MessageEvent;
